import { useEffect, useState } from "react";
import { Clock, CreditCard, Package, Truck, CheckCircle2, Archive, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatRupiah, formatDate } from "@/lib/dummyData";
import { AdminAPI } from "@/lib/api";
import { downloadCsv, printHtml } from "@/lib/download";
import { toast } from "sonner";

// Kolom pipeline mengikuti status backend (Inggris) + label Indonesia.
const pipelineConfig = [
  { key: "PENDING", label: "Menunggu Bayar", icon: Clock, color: "bg-stone-soft/40 text-ink/75" },
  { key: "PAID", label: "Dibayar", icon: CreditCard, color: "bg-sage/20 text-forest" },
  { key: "PACKED", label: "Dikemas", icon: Package, color: "bg-amber-warm/20 text-amber-700" },
  { key: "SHIPPED", label: "Dikirim", icon: Truck, color: "bg-amber-warm/20 text-amber-700" },
  { key: "DELIVERED", label: "Diterima", icon: CheckCircle2, color: "bg-sage/20 text-forest" },
  { key: "COMPLETED", label: "Selesai", icon: Archive, color: "bg-forest/10 text-forest" },
];
// Status yang boleh diset admin (COMPLETED tetap via konfirmasi terima anggota).
const ADMIN_STATUS = ["PAID", "PACKED", "SHIPPED", "DELIVERED", "CANCELLED", "COMPLAINT"];

export default function AdminOrders() {
  const [q, setQ] = useState("");
  const [orders, setOrders] = useState([]);

  const load = () =>
    AdminAPI.orders().then((rows) => {
      setOrders((rows || []).map((o) => ({
        id: o.order_number, uid: o.id, member: o.buyer_name || "-", status: o.status,
        payment: o.payment_method, total: Number(o.total), date: o.created_at, channel: o.channel,
      })));
    }).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  // Ubah status pesanan (hanya untuk data nyata).
  const changeStatus = async (uid, status) => {
    try { await AdminAPI.updateOrderStatus(uid, status); toast.success(`Status → ${status}`); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Gagal ubah status"); }
  };

  const source = orders;
  const filtered = source.filter((o) => (o.id || "").toLowerCase().includes(q.toLowerCase()) || (o.member || "").toLowerCase().includes(q.toLowerCase()));

  // Cetak surat jalan untuk pesanan siap kirim (PAID/PACKED/SHIPPED) dari daftar saat ini.
  const printSuratJalan = () => {
    const kirim = filtered.filter((o) => ["PAID", "PACKED", "SHIPPED"].includes(o.status));
    if (kirim.length === 0) return toast.info("Tidak ada pesanan siap kirim");
    const rows = kirim.map((o) => `<tr><td>${o.id}</td><td>${o.member}</td><td>${o.payment}</td><td>${formatDate(o.date)}</td><td class="right">${formatRupiah(o.total)}</td></tr>`).join("");
    printHtml("Surat Jalan", `<h1>Surat Jalan</h1><div class="muted">Koperasi Andalan Dapur Nusantara · dicetak ${new Date().toLocaleString("id-ID")}</div>
      <table><thead><tr><th>No. Pesanan</th><th>Penerima</th><th>Pembayaran</th><th>Tanggal</th><th class="right">Total</th></tr></thead><tbody>${rows}</tbody></table>
      <p class="muted" style="margin-top:16px">Total ${kirim.length} pesanan siap kirim.</p>`);
  };

  // Ekspor daftar pesanan (sesuai filter) ke CSV.
  const exportCsv = () => {
    if (filtered.length === 0) return toast.info("Tidak ada pesanan untuk diekspor");
    downloadCsv("pesanan", ["No. Pesanan", "Anggota", "Status", "Pembayaran", "Kanal", "Tanggal", "Total"],
      filtered.map((o) => [o.id, o.member, o.status, o.payment, o.channel, formatDate(o.date), o.total]));
    toast.success(`${filtered.length} pesanan diekspor`);
  };
  const grouped = pipelineConfig.map((p) => ({ ...p, orders: filtered.filter((o) => o.status === p.key) }));
  const totalActive = source.filter((o) => !["COMPLETED", "CANCELLED", "COMPLAINT"].includes(o.status)).length;
  const totalValue = source.reduce((s, o) => s + Number(o.total || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Kelola Pesanan</h1>
          <p className="text-ink/75 mt-2 text-sm">Pipeline pesanan · {totalActive} aktif · {formatRupiah(totalValue)} total nilai.</p>
        </div>
        <div className="relative w-full lg:w-80">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/55" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari pesanan atau anggota..." className="pl-9 bg-white border-stone-soft rounded-lg" data-testid="admin-orders-search" />
        </div>
      </div>

      {/* Pipeline kanban */}
      <div className="overflow-x-auto pb-2">
        <div className="grid grid-cols-6 gap-3 min-w-[1100px]">
          {grouped.map((g) => (
            <div key={g.key} className="rounded-xl bg-white border border-stone-soft" data-testid={`pipeline-${g.key.toLowerCase()}`}>
              <div className="px-4 py-3 border-b border-stone-soft flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 rounded-lg grid place-items-center ${g.color}`}><g.icon size={14} /></span>
                  <span className="font-display font-semibold text-sm text-ink">{g.label}</span>
                </div>
                <Badge className="bg-cream text-ink/75 hover:bg-cream border-0 rounded-full text-[10px]">{g.orders.length}</Badge>
              </div>
              <div className="p-3 space-y-2 min-h-[120px] max-h-[520px] overflow-y-auto">
                {g.orders.length === 0 ? (
                  <div className="text-xs text-ink/55 text-center py-6">Tidak ada pesanan</div>
                ) : g.orders.map((o) => (
                  <div key={o.id} className="rounded-lg border border-stone-soft bg-cream/40 p-3" data-testid={`kanban-order-${o.id}`}>
                    <div className="font-mono text-[10px] text-ink/55">{o.id}</div>
                    <div className="font-semibold text-sm text-ink mt-1 truncate">{o.member}</div>
                    <div className="text-xs text-ink/75 mt-1">{o.payment}{o.channel === "O2O_QR" ? " · O2O" : ""}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-[10px] text-ink/55">{formatDate(o.date)}</div>
                      <div className="font-display font-bold text-xs text-ink">{formatRupiah(o.total)}</div>
                    </div>
                    {/* Ubah status (bukan pesanan selesai) */}
                    {o.status !== "COMPLETED" && (
                      <select
                        value=""
                        onChange={(e) => e.target.value && changeStatus(o.uid, e.target.value)}
                        className="mt-2 w-full text-[11px] rounded-md border border-stone-soft bg-white px-2 py-1"
                        data-testid={`order-status-${o.id}`}
                      >
                        <option value="">Ubah status…</option>
                        {ADMIN_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Card className="rounded-xl border-stone-soft p-6">
        <h3 className="font-display font-semibold text-ink">Aksi Cepat</h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
          <Button variant="outline" className="rounded-lg border-stone-soft" onClick={printSuratJalan} data-testid="orders-action-print">Cetak Surat Jalan</Button>
          <Button variant="outline" className="rounded-lg border-stone-soft" onClick={exportCsv} data-testid="orders-action-export">Ekspor CSV</Button>
          <Button variant="outline" className="rounded-lg border-stone-soft" onClick={load} data-testid="orders-action-refresh">Muat Ulang</Button>
        </div>
      </Card>
    </div>
  );
}
